import type {
  ColumnEvent,
  ColumnEventType,
  FilterChangedEvent,
  SortChangedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnVisibleEvent,
} from 'ag-grid-community'
import { format, parseISO } from 'date-fns'

import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'
import type { ButtonClickedData } from '@b-stock/fe-core/bstock-react'

import type { ListViewSurface } from './listViewSurface'

export interface AnalyticsTracker {
  trackButtonClicked: (data: ButtonClickedData) => void
}

/**
 * The column-event sources that are the buyer, rather than the app.
 *
 * AG Grid cannot answer "was this user input" for us. `setColumnsVisible` and
 * the rest take no source argument in this version, and `ColumnEventType` is a
 * closed union with no room for one — so every change the app makes through the
 * API arrives as `'api'`, indistinguishable from any other. The app makes
 * several: `useColumnPinning` re-pins on every resize, and the action panel
 * hides and restores its own column when the buyer minimises it.
 *
 * So this allowlists the sources that *are* a buyer — a drag, the column menu,
 * the context menu, the tool panel — rather than denying `'api'`. The failure
 * modes are not equal: a missed event is a gap in the funnel, a phantom one is
 * a lie in it.
 *
 * An in-app control that changes columns through the API therefore has to track
 * its own click, because the grid event it causes cannot be told apart from the
 * app's. The manifest grid's `ColumnSelect` is one such control — it calls
 * `setColumnsVisible` directly — and is unaffected only because that grid does
 * not wire these handlers at all. A column chooser on *this* grid would need
 * its own tracking rather than relying on `onColumnVisible`.
 */
const USER_COLUMN_EVENT_SOURCES: ReadonlySet<ColumnEventType> = new Set([
  'columnMenu',
  'contextMenu',
  'toolPanelDragAndDrop',
  'toolPanelUi',
  'uiColumnDragged',
  'uiColumnMoved',
])

const isUserColumnEvent = (event: ColumnEvent): boolean =>
  USER_COLUMN_EVENT_SOURCES.has(event.source)

export interface AnalyticsContext {
  listing_card_impression_id?: string
  [key: string]: unknown
}

const formatDateValue = (value: string | number): string => {
  if (typeof value === 'number') {
    return String(value)
  }

  try {
    const date = parseISO(value)
    return format(date, 'yyyy-MM-dd')
  } catch {
    return value
  }
}

const getImpressionId = (analytics: AnalyticsContext): string | undefined => {
  return 'listing_card_impression_id' in analytics
    ? String(analytics.listing_card_impression_id)
    : undefined
}

const getColumnHeaderName = (
  api: FilterChangedEvent['api'],
  columnId: string
): string => {
  const column = api.getColumn(columnId)
  return column?.getColDef().headerName || columnId
}

const extractApplyRange = (
  filter: Record<string, unknown>
): string | undefined => {
  if (
    !('value' in filter) ||
    filter.value === null ||
    typeof filter.value !== 'object'
  ) {
    return undefined
  }

  const value = filter.value as Record<string, unknown>
  if (!('from' in value && 'to' in value)) {
    return undefined
  }

  const { from, to } = value
  if (
    from === null ||
    to === null ||
    (typeof from !== 'string' && typeof from !== 'number') ||
    (typeof to !== 'string' && typeof to !== 'number')
  ) {
    return undefined
  }

  return `from: ${formatDateValue(from)}, to: ${formatDateValue(to)}`
}

const getFilterTypeLabel = (filterType?: string): string => {
  if (!filterType) return ''

  const typeMap: Record<string, string> = {
    equals: 'Equals',
    notEqual: 'Does not equal',
    lessThan: 'Less than',
    lessThanOrEqual: 'Less than or equal',
    greaterThan: 'Greater than',
    greaterThanOrEqual: 'Greater than or equal',
    inRange: 'Between',
    blank: 'Blank',
    notBlank: 'Not blank',
    contains: 'Contains',
    notContains: 'Does not contain',
    startsWith: 'Starts with',
    endsWith: 'Ends with',
  }

  return typeMap[filterType] || filterType
}

const trackFilterEvent = (
  tracker: AnalyticsTracker,
  params: {
    filterName: string
    filterTitle: string
    isSelected: boolean
    filterValues?: string
    impressionId?: string
    surface?: ListViewSurface
  }
) => {
  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'column_filters',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    filter_name: params.filterName,
    filter_title: params.filterTitle,
    is_selected: params.isSelected,
    ...params.surface,
    ...(params.filterValues && { filter_values: params.filterValues }),
    ...(params.impressionId && {
      listing_card_impression_id: params.impressionId,
    }),
  })
}

export const getFilterChanges = (
  previousFilter: Record<string, unknown> | undefined,
  currentFilter: Record<string, unknown>
) => {
  const changes: { added: string[]; removed: string[] } = {
    added: [],
    removed: [],
  }

  if ('values' in currentFilter && Array.isArray(currentFilter.values)) {
    const currentValues = currentFilter.values.map(String)
    const previousValues =
      previousFilter &&
      'values' in previousFilter &&
      Array.isArray(previousFilter.values)
        ? (previousFilter.values as unknown[]).map(String)
        : []

    changes.added = currentValues.filter(
      (value) => !previousValues.includes(value)
    )
    changes.removed = previousValues.filter(
      (value) => !currentValues.includes(value)
    )
  }

  // Handle AG-Grid's NumberFilter range (filter + filterTo)
  if ('filter' in currentFilter && 'filterTo' in currentFilter) {
    const currentParts: string[] = []
    const previousParts: string[] = []

    if ('filter' in currentFilter && currentFilter.filter != null) {
      const filterValue = currentFilter.filter
      if (typeof filterValue === 'string' || typeof filterValue === 'number') {
        currentParts.push(`from: ${String(filterValue)}`)
      }
    }
    if ('filterTo' in currentFilter && currentFilter.filterTo != null) {
      const toValue = currentFilter.filterTo
      if (typeof toValue === 'string' || typeof toValue === 'number') {
        currentParts.push(`to: ${String(toValue)}`)
      }
    }

    if (previousFilter) {
      if ('filter' in previousFilter && previousFilter.filter != null) {
        const filterValue = previousFilter.filter
        if (
          typeof filterValue === 'string' ||
          typeof filterValue === 'number'
        ) {
          previousParts.push(`from: ${String(filterValue)}`)
        }
      }
      if ('filterTo' in previousFilter && previousFilter.filterTo != null) {
        const toValue = previousFilter.filterTo
        if (typeof toValue === 'string' || typeof toValue === 'number') {
          previousParts.push(`to: ${String(toValue)}`)
        }
      }
    }

    const currentRange = currentParts.join(', ')
    const previousRange = previousParts.join(', ')

    if (currentRange !== previousRange) {
      if (currentRange) changes.added = [currentRange]
      if (previousRange) changes.removed = [previousRange]
    }

    return changes
  }

  // Handle single filter value (text or number)
  if (
    'filter' in currentFilter &&
    (typeof currentFilter.filter === 'string' ||
      typeof currentFilter.filter === 'number')
  ) {
    // For number filters, include the type (equals, lessThan, greaterThan, etc.)
    const currentType =
      'type' in currentFilter && typeof currentFilter.type === 'string'
        ? currentFilter.type
        : ''
    const previousType =
      previousFilter &&
      'type' in previousFilter &&
      typeof previousFilter.type === 'string'
        ? previousFilter.type
        : ''

    const currentValue = String(currentFilter.filter)
    const previousValue =
      previousFilter &&
      'filter' in previousFilter &&
      (typeof previousFilter.filter === 'string' ||
        typeof previousFilter.filter === 'number')
        ? String(previousFilter.filter)
        : ''

    // Detect change if either the value or type changed
    if (currentValue !== previousValue || currentType !== previousType) {
      if (currentValue) changes.added = [currentValue]
      if (previousValue) changes.removed = [previousValue]
    }
  }

  if (
    'value' in currentFilter &&
    currentFilter.value !== null &&
    typeof currentFilter.value === 'object'
  ) {
    const currentValue = currentFilter.value as Record<string, unknown>
    const previousValue =
      previousFilter &&
      'value' in previousFilter &&
      previousFilter.value !== null &&
      typeof previousFilter.value === 'object'
        ? (previousFilter.value as Record<string, unknown>)
        : null

    if ('from' in currentValue || 'to' in currentValue) {
      const currentParts: string[] = []
      const previousParts: string[] = []

      if ('from' in currentValue && currentValue.from != null) {
        const fromValue = currentValue.from
        if (typeof fromValue === 'string' || typeof fromValue === 'number') {
          currentParts.push(`from: ${String(fromValue)}`)
        }
      }
      if ('to' in currentValue && currentValue.to != null) {
        const toValue = currentValue.to
        if (typeof toValue === 'string' || typeof toValue === 'number') {
          currentParts.push(`to: ${String(toValue)}`)
        }
      }

      if (previousValue) {
        if ('from' in previousValue && previousValue.from != null) {
          const fromValue = previousValue.from
          if (typeof fromValue === 'string' || typeof fromValue === 'number') {
            previousParts.push(`from: ${String(fromValue)}`)
          }
        }
        if ('to' in previousValue && previousValue.to != null) {
          const toValue = previousValue.to
          if (typeof toValue === 'string' || typeof toValue === 'number') {
            previousParts.push(`to: ${String(toValue)}`)
          }
        }
      }

      const currentRange = currentParts.join(', ')
      const previousRange = previousParts.join(', ')

      if (currentRange !== previousRange) {
        const filterKey =
          'key' in currentFilter && typeof currentFilter.key === 'string'
            ? currentFilter.key
            : currentRange

        if (currentRange) changes.added = [filterKey]
        if (previousRange && !currentRange) {
          const previousKey =
            previousFilter &&
            'key' in previousFilter &&
            typeof previousFilter.key === 'string'
              ? previousFilter.key
              : previousRange
          changes.removed = [previousKey]
        }
      }

      return changes
    }
  }

  if ('filterTo' in currentFilter || 'filterFrom' in currentFilter) {
    const currentParts: string[] = []
    const previousParts: string[] = []

    if ('filterFrom' in currentFilter && currentFilter.filterFrom != null) {
      const fromValue = currentFilter.filterFrom
      if (typeof fromValue === 'string' || typeof fromValue === 'number') {
        currentParts.push(`from: ${String(fromValue)}`)
      }
    }
    if ('filterTo' in currentFilter && currentFilter.filterTo != null) {
      const toValue = currentFilter.filterTo
      if (typeof toValue === 'string' || typeof toValue === 'number') {
        currentParts.push(`to: ${String(toValue)}`)
      }
    }

    if (previousFilter) {
      if ('filterFrom' in previousFilter && previousFilter.filterFrom != null) {
        const fromValue = previousFilter.filterFrom
        if (typeof fromValue === 'string' || typeof fromValue === 'number') {
          previousParts.push(`from: ${String(fromValue)}`)
        }
      }
      if ('filterTo' in previousFilter && previousFilter.filterTo != null) {
        const toValue = previousFilter.filterTo
        if (typeof toValue === 'string' || typeof toValue === 'number') {
          previousParts.push(`to: ${String(toValue)}`)
        }
      }
    }

    const currentRange = currentParts.join(', ')
    const previousRange = previousParts.join(', ')

    if (currentRange !== previousRange) {
      if (currentRange) changes.added = [currentRange]
      if (previousRange) changes.removed = [previousRange]
    }
  }

  return changes
}

export const createFilterChangeHandler = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  refs: {
    isInitialFilterLoadRef: React.RefObject<boolean>
    lastFilterChangeRef: React.RefObject<string>
    trackingTimeoutRef: React.RefObject<NodeJS.Timeout | undefined>
    previousFilterModelRef: React.RefObject<Record<string, unknown>>
  },
  surface?: ListViewSurface
) => {
  return (event: FilterChangedEvent) => {
    if (refs.isInitialFilterLoadRef.current) {
      const filterModel = event.api.getFilterModel()
      const hasFilters = Object.keys(filterModel).length > 0
      if (!hasFilters || event.source === 'api') {
        refs.isInitialFilterLoadRef.current = false
        refs.previousFilterModelRef.current = { ...filterModel }
        return
      } else {
        refs.isInitialFilterLoadRef.current = false
        refs.previousFilterModelRef.current = {}
      }
    }

    if (event.source === 'quickFilter') {
      return
    }

    if (event.source === 'api') {
      return
    }

    if (refs.trackingTimeoutRef.current) {
      clearTimeout(refs.trackingTimeoutRef.current)
    }

    refs.trackingTimeoutRef.current = setTimeout(async () => {
      const impressionId = getImpressionId(analytics)

      try {
        const filterModel = event.api.getFilterModel()
        const filterModelString = JSON.stringify(filterModel)

        if (Object.keys(filterModel).length === 0) {
          refs.lastFilterChangeRef.current = filterModelString
          refs.previousFilterModelRef.current = {}
          return
        }

        if (refs.lastFilterChangeRef.current === filterModelString) {
          return
        }
        refs.lastFilterChangeRef.current = filterModelString

        // Handle removed filters (columns that are no longer in filterModel)
        Object.keys(refs.previousFilterModelRef.current).forEach((columnId) => {
          if (!(columnId in filterModel)) {
            const previousFilter = refs.previousFilterModelRef.current[
              columnId
            ] as Record<string, unknown> | undefined

            if (!previousFilter) return

            const headerName = getColumnHeaderName(event.api, columnId)

            if (
              'values' in previousFilter &&
              Array.isArray(previousFilter.values)
            ) {
              const removedValues = previousFilter.values.map(String)
              const lastValue = removedValues[removedValues.length - 1]
              if (lastValue) {
                trackFilterEvent(tracker, {
                  filterName: lastValue,
                  filterTitle: headerName,
                  isSelected: false,
                  impressionId,
                  surface,
                })
              }
            }

            if (
              'key' in previousFilter &&
              typeof previousFilter.key === 'string'
            ) {
              const filterName = previousFilter.key

              if (filterName === 'customRange') {
                const filterValues = extractApplyRange(previousFilter)
                trackFilterEvent(tracker, {
                  filterName,
                  filterTitle: headerName,
                  isSelected: false,
                  filterValues,
                  impressionId,
                  surface,
                })
              } else {
                trackFilterEvent(tracker, {
                  filterName,
                  filterTitle: headerName,
                  isSelected: false,
                  impressionId,
                  surface,
                })
              }
            }
          }
        })

        for (const columnId of Object.keys(filterModel)) {
          const columnFilter = filterModel[columnId] as unknown
          if (columnFilter && typeof columnFilter === 'object') {
            const typedFilter = columnFilter as Record<string, unknown>
            let previousFilter = refs.previousFilterModelRef.current[
              columnId
            ] as Record<string, unknown> | undefined

            // If no previous filter (meaning "All Selected"), try to fetch all values
            // to correctly determine what was removed vs kept.
            if (!previousFilter && 'values' in typedFilter) {
              try {
                const instance = (await event.api.getColumnFilterInstance(
                  columnId
                )) as unknown
                if (
                  instance &&
                  typeof instance === 'object' &&
                  'getFilterKeys' in instance &&
                  typeof instance.getFilterKeys === 'function'
                ) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                  const allValues: unknown = instance.getFilterKeys()
                  if (Array.isArray(allValues)) {
                    previousFilter = { values: allValues }
                  }
                }
              } catch (error) {
                console.warn('Error fetching filter instance:', error)
              }
            }

            const changes = getFilterChanges(previousFilter, typedFilter)

            if (changes.added.length > 0 || changes.removed.length > 0) {
              let filterValues = ''
              let isSelected = false

              const isClosesInFilter =
                'key' in typedFilter && typeof typedFilter.key === 'string'

              const filterType =
                'type' in typedFilter && typeof typedFilter.type === 'string'
                  ? typedFilter.type
                  : ''

              if (changes.added.length > 0) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                filterValues = changes.added[0]!
                isSelected = true
              } else if (changes.removed.length > 0) {
                const currentValues = Array.isArray(typedFilter.values)
                  ? typedFilter.values
                  : []
                if (
                  changes.removed.length > currentValues.length &&
                  currentValues.length > 0
                ) {
                  filterValues = String(currentValues[0])
                  isSelected = true
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we have already checked changes.removed.length > 0
                  filterValues = changes.removed[0]!
                  isSelected = false
                }
              }

              if (filterValues) {
                const headerName = getColumnHeaderName(event.api, columnId)

                const isSetFilter =
                  'values' in typedFilter && Array.isArray(typedFilter.values)

                if (isClosesInFilter) {
                  const filterName = typedFilter.key as string

                  if (filterName === 'customRange') {
                    const dateRange = extractApplyRange(typedFilter)
                    trackFilterEvent(tracker, {
                      filterName,
                      filterTitle: headerName,
                      isSelected,
                      filterValues: dateRange,
                      impressionId,
                      surface,
                    })
                  } else {
                    trackFilterEvent(tracker, {
                      filterName,
                      filterTitle: headerName,
                      isSelected,
                      impressionId,
                      surface,
                    })
                  }
                } else if (isSetFilter) {
                  trackFilterEvent(tracker, {
                    filterName: filterValues,
                    filterTitle: headerName,
                    isSelected,
                    impressionId,
                    surface,
                  })
                } else {
                  const filterName = getFilterTypeLabel(filterType)

                  trackFilterEvent(tracker, {
                    filterName,
                    filterTitle: headerName,
                    isSelected,
                    filterValues,
                    impressionId,
                    surface,
                  })
                }
              }
            }
          }
        }

        refs.previousFilterModelRef.current = { ...filterModel }
      } catch (error) {
        console.warn('Error tracking filter changes:', error)
      }
    }, 1000)
  }
}

export const createSortChangeHandler = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  refs: {
    lastSortChangeRef: React.RefObject<string>
    isInitialLoadRef?: React.RefObject<boolean>
  },
  surface?: ListViewSurface
) => {
  return (event: SortChangedEvent) => {
    if (refs.isInitialLoadRef?.current) {
      return
    }

    try {
      const columnState = event.api.getColumnState()
      const sortedColumns = columnState
        .filter((col) => col.sort !== null)
        .map((col) => ({ column: col.colId, sort: col.sort }))

      if (sortedColumns.length > 0) {
        const sortCol = sortedColumns[0] as { column: string; sort: string }
        const sortState = `${sortCol.column}:${sortCol.sort}`

        if (refs.lastSortChangeRef.current === sortState) {
          return
        }
        refs.lastSortChangeRef.current = sortState

        const impressionId = getImpressionId(analytics)

        let filterName = 'no_sort'
        if (sortCol.sort === 'asc') filterName = 'ascending'
        else if (sortCol.sort === 'desc') filterName = 'descending'

        const headerName = getColumnHeaderName(event.api, sortCol.column)

        tracker.trackButtonClicked({
          screenName: 'all_auction_list_view',
          buttonName: 'list_view_column_name',
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
          filter_name: filterName,
          filter_title: headerName,
          is_selected: true,
          ...surface,
          ...(impressionId && { listing_card_impression_id: impressionId }),
        })
      }
    } catch (error) {
      console.warn('Error tracking sort changes:', error)
    }
  }
}

export const createColumnMoveHandler = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  refs?: {
    isInitialLoadRef: React.RefObject<boolean>
  },
  surface?: ListViewSurface
) => {
  return (event: ColumnMovedEvent) => {
    if (refs?.isInitialLoadRef.current) {
      return
    }

    if (!isUserColumnEvent(event)) {
      return
    }

    const impressionId = getImpressionId(analytics)
    const headerName =
      event.column?.getColDef().headerName || event.column?.getColId() || ''

    tracker.trackButtonClicked({
      screenName: 'all_auction_list_view',
      buttonName: 'list_view_column_move',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      filter_name: '',
      filter_title: headerName,
      is_selected: true,
      ...surface,
      ...(impressionId && { listing_card_impression_id: impressionId }),
    })
  }
}

export const createColumnPinHandler = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  refs?: {
    isInitialLoadRef: React.RefObject<boolean>
  },
  surface?: ListViewSurface
) => {
  return (event: ColumnPinnedEvent) => {
    if (refs?.isInitialLoadRef.current) {
      return
    }

    if (!isUserColumnEvent(event)) {
      return
    }

    const impressionId = getImpressionId(analytics)
    const headerName = (
      event.column?.getColDef().headerName ||
      event.column?.getColId() ||
      ''
    ).toLowerCase()

    tracker.trackButtonClicked({
      screenName: 'all_auction_list_view',
      buttonName: 'choose_columns',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      filter_name: 'choose column',
      filter_title: headerName,
      is_selected: event.pinned !== null,
      ...surface,
      ...(impressionId && { listing_card_impression_id: impressionId }),
    })
  }
}

export const createColumnVisibilityHandler = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  refs?: {
    isInitialLoadRef: React.RefObject<boolean>
  },
  surface?: ListViewSurface
) => {
  return (event: ColumnVisibleEvent) => {
    if (refs?.isInitialLoadRef.current) {
      return
    }

    if (!isUserColumnEvent(event)) {
      return
    }

    const impressionId = getImpressionId(analytics)
    const headerName = (
      event.column?.getColDef().headerName ||
      event.column?.getColId() ||
      ''
    ).toLowerCase()

    tracker.trackButtonClicked({
      screenName: 'all_auction_list_view',
      buttonName: 'choose_columns',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      filter_name: 'choose column',
      filter_title: headerName,
      is_selected: event.visible,
      ...surface,
      ...(impressionId && { listing_card_impression_id: impressionId }),
    })
  }
}

export const trackExportEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  exportType: 'button' | 'context_menu',
  surface?: ListViewSurface
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'export',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    export_type: exportType,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
  })
}

export const trackDownloadAllManifestsClicked = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'download_all_manifests',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
  })
}

export const trackSignInToBidEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'sign_in_to_bid',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
  })
}

export const trackSignInToBuyEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'sign_in_to_buy',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
  })
}

export const trackBidNowEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface,
  threshold: number | null = null
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'bid_now',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
    ...(threshold !== null && { threshold }),
  })
}

export const trackBuyNowEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'buy_now',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
  })
}

/**
 * What `action` reports -- snake_case `PanelActionId`.
 */
type PanelActionAnalyticsId =
  | 'ai_summary'
  | 'download_manifest'
  | 'view_manifest'
  | 'watchlist'

/**
 * The action panel's own event. **Every** panel action emits this in addition
 * to its own generic event — Watchlist emits `toggle_listing_watched` and this,
 * Download Manifest emits `download_manifest` and this — so the panel can be
 * analysed as one surface without any action losing the event its existing
 * dashboards already read.
 *
 * `action` is what distinguishes the rows; `screenName` is the same literal on
 * every List View, so `surface` is what says which page the click came from.
 */
export const trackActionPanelActionClicked = (
  tracker: AnalyticsTracker,
  {
    action,
    listingId,
    surface,
  }: {
    action: PanelActionAnalyticsId
    listingId: string
    surface?: ListViewSurface
  }
) => {
  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'action_panel_action_clicked',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    action,
    listing_id: listingId,
    ...surface,
  })
}

/**
 * The name the ALP cards send for the winning `quick-view` variant, so one
 * feature reports under one event; the screenName separates the two surfaces.
 */
export const trackManifestQuickView = (
  tracker: AnalyticsTracker,
  { listingId, surface }: { listingId: string; surface?: ListViewSurface }
) => {
  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'manifest_quick_view',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    listing_id: listingId,
    ...surface,
  })
}

/**
 * The button name the listing detail page's Download Manifest control already
 * fires, so List View extends that series rather than starting a parallel one.
 */
export const trackDownloadManifest = (
  tracker: AnalyticsTracker,
  { listingId, surface }: { listingId: string; surface?: ListViewSurface }
) => {
  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'download_manifest',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    listing_id: listingId,
    ...surface,
  })
}

/**
 * The button name the listing detail page's View Manifest control already
 * fires, so List View extends that series rather than starting a parallel one.
 */
export const trackViewManifest = (
  tracker: AnalyticsTracker,
  { listingId, surface }: { listingId: string; surface?: ListViewSurface }
) => {
  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'view_manifest',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    listing_id: listingId,
    ...surface,
  })
}

/**
 * Deliberately carries only the three properties every other watchlist surface
 * sends, and none of the panel's own: it feeds dashboards that already read
 * this event from the listing detail page and the card grid, and the panel is
 * distinguished through `action_panel_action_clicked` instead.
 */
export const trackToggleListingWatched = (
  tracker: AnalyticsTracker,
  {
    isSelected,
    listingId,
    storefrontName,
  }: { isSelected: boolean; listingId: string; storefrontName?: string }
) => {
  tracker.trackButtonClicked({
    buttonName: 'toggle_listing_watched',
    is_selected: isSelected,
    listing_id: listingId,
    storefront_name: storefrontName,
  })
}

export const trackRaiseBidEvent = (
  tracker: AnalyticsTracker,
  analytics: AnalyticsContext,
  surface?: ListViewSurface,
  threshold: number | null = null
) => {
  const impressionId = getImpressionId(analytics)

  tracker.trackButtonClicked({
    screenName: 'all_auction_list_view',
    buttonName: 'raise_bid',
    source: AnalyticsSource.HOME_PORTAL,
    entityType: AnalyticsEntityType.BUYER,
    ...surface,
    ...(impressionId && { listing_card_impression_id: impressionId }),
    ...(threshold !== null && { threshold }),
  })
}
